import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from '../../utils/imageUtils.js';
import axios from "axios";
import "./Sidebar.css";

const Sidebar = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";


  useEffect(() => {
    fetchCategories();
  }, []);

  // Close sidebar on route change and handle resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${baseURL}/api/categories/getcategories`);
      
      if (response.data) {
        // Handle different response structures
        const categoryData = response.data.categories || response.data || [];
        // Filter only enabled categories
        const enabledCategories = Array.isArray(categoryData) 
          ? categoryData.filter(category => category.enabled === true)
          : [];
        setCategories(enabledCategories);
      } else {
        console.error('Unexpected API response structure:', response.data);
        setError('Received invalid data format from server');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.code === 'ECONNABORTED') {
        setError('Request timed out. Please try again.');
      } else if (error.response?.status === 404) {
        setError('Categories service not found.');
      } else if (error.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load categories. Check your connection.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigation = (categoryName) => {
    // Convert category name to URL-friendly format
    const path = categoryName
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    
    navigate(`/${path}`);
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const handleRetry = () => {
    fetchCategories();
  };

  // Enhanced hamburger menu for mobile
  const Hamburger = (
    <button
      className="enhanced-sidebar-hamburger"
      aria-label="Open navigation menu"
      onClick={() => setSidebarOpen(true)}
    >
      <div className="enhanced-hamburger-lines">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </button>
  );

  // Overlay for mobile sidebar
  const Overlay = (
    <div
      className={`enhanced-sidebar-overlay${sidebarOpen ? " active" : ""}`}
      onClick={() => setSidebarOpen(false)}
      aria-hidden="true"
    />
  );

  if (isLoading) {
    return (
      <>
        {Hamburger}
        <div className={`enhanced-sidebar${sidebarOpen ? " active" : ""}`}>
          <div className="enhanced-sidebar-header">
            <h2 className="enhanced-sidebar-title">Navigation</h2>
            <button
              className="enhanced-sidebar-close"
              aria-label="Close navigation menu"
              onClick={() => setSidebarOpen(false)}
            >
              <span>×</span>
            </button>
          </div>
          <div className="enhanced-sidebar-loading">
            <div className="enhanced-loading-spinner"></div>
            <p className="enhanced-loading-text">Loading categories...</p>
          </div>
        </div>
        {Overlay}
      </>
    );
  }

  if (error) {
    return (
      <>
        {Hamburger}
        <div className={`enhanced-sidebar${sidebarOpen ? " active" : ""}`}>
          <div className="enhanced-sidebar-header">
            <h2 className="enhanced-sidebar-title">Navigation</h2>
            <button
              className="enhanced-sidebar-close"
              aria-label="Close navigation menu"
              onClick={() => setSidebarOpen(false)}
            >
              <span>×</span>
            </button>
          </div>
          <div className="enhanced-sidebar-error">
            <div className="enhanced-error-icon">⚠️</div>
            <p className="enhanced-error-message">{error}</p>
            <button className="enhanced-retry-button" onClick={handleRetry}>
              <span className="enhanced-retry-icon">🔄</span>
              Try Again
            </button>
          </div>
        </div>
        {Overlay}
      </>
    );
  }

  return (
    <>
      {Hamburger}
      <div className={`enhanced-sidebar${sidebarOpen ? " active" : ""}`}>
        <div className="enhanced-sidebar-content">
          {categories.length > 0 ? (
            <div className="enhanced-sidebar-options">
              {categories.map((category, index) => (
                <button
                  key={category._id}
                  className="enhanced-sidebar-option"
                  onClick={() => handleNavigation(category.name)}
                  aria-label={`Navigate to ${category.name}`}
                >
                  <div className="enhanced-option-content">
                    {category.iconUrl ? (
                      <img
                        className="enhanced-option-icon-img"
                        src={getImageUrl(category.iconUrl)}
                        alt={category.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="enhanced-option-icon">🏷️</span>
                    )}
                    <span className="enhanced-option-label">{category.name}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="enhanced-sidebar-empty">
              <p className="enhanced-empty-message">No categories available</p>
              <p className="enhanced-empty-submessage">Categories will appear here when they're enabled</p>
            </div>
          )}
        </div>
      </div>
      {Overlay}
    </>
  );
};

export default Sidebar;